<template>
  <section :id="$options.name" class="recommended" v-if="recommended_items.length">
    <div
      class="recommended__element"
      v-for="(item, index) in recommended_items"
      :key="_uid + '_recommended_' + index"
    >
      <vb-card
        variant="horizontal"
        :card="{ image: room_image(item.room) }"
        @click_image="open_modal_recommended(item.product.id)"
      >
        <div class="recommended__body">
          <p class="recommended__title">Soluzione consigliata</p>
          <div class="recommended__row">
            <div class="recommended__main">
              <p class="recommended__name">{{ item.room.name.it }}</p>
              <div class="recommended__details">
                <occupancy-product :maximum_occupancy="item.product.maximum_occupancy" />
                <refundability :cancellation="item.product.policies.cancellation" />
                <mealplan
                  v-if="item.product.policies.meal_plan.meals.length"
                  :meals="item.product.policies.meal_plan.meals"
                />
                <div class="recommended__beds">
                  <vb-icon class="recommended__beds-icon" :name="'doublebed'" :color="sass('dark')" :size="13" />
                  <span v-html="format_beds(item.room.bed_options)" />
                </div>
              </div>
              <span class="recommended__occupancy">{{ occupancy_label(item.rec) }}</span>
            </div>
            <div class="recommended__cta">
              <div class="recommended__price">
                <span class="recommended__price-value">€ {{ item_price(item) }}</span>
                <span class="recommended__price-tax">include gestione e assistenza</span>
              </div>
              <vb-button
                :vb_options="{ text: 'Scegli', variant: 'info', classes: 'w-100' }"
                @click="select_recommended(item)"
              />
            </div>
          </div>
        </div>
      </vb-card>

      <vb-modal
        :vb_options="{
          id: 'modal_room_recommended_' + item.product.id,
          body_class: 'rooms__modal',
          title: item.room.name.it,
          size: 'lg',
          centered: true,
          hide_footer: true
        }"
      >
        <room-modal :room="item.room" />
      </vb-modal>
    </div>
  </section>
</template>

<script>
import { format_beds, markup, round_up } from '@bookingv3/utilities'
import RoomModal from '@bookingv3/components/blocks/detail/room/room-modal'
import OccupancyProduct from '@bookingv3/components/blocks/occupancy-product'
import Mealplan from '@bookingv3/components/atoms/mealplan'
import Refundability from '@bookingv3/components/atoms/refundability'

export default {
  name: 'recommended',

  components: {
    'room-modal': RoomModal,
    'occupancy-product': OccupancyProduct,
    'mealplan': Mealplan,
    'refundability': Refundability,
  },

  props: {
    details: Object,
    availabilities: Object,
  },

  data() {
    return {
    }
  },

  computed: {

    recommended_items() {
      const rec = this.availabilities && this.availabilities.recommendation
      if (!rec || !rec.products || !rec.products.length) return []

      return rec.products.map(rp => {
        const product = this.availabilities.products.find(p => p.id === rp.id)
        if (!product) return null
        const room = this.details.rooms.find(r => r.id === product.room)
        return room ? { room, product, rec: rp } : null
      }).filter(Boolean)
    },

  },

  created() {
    this.format_beds = format_beds
  },

  mounted() {
  },

  methods: {

    room_image(room) {
      return (room.photos && room.photos.length)
        ? room.photos[0].url.standard
        : '/assets/images/common/statics/room_placeholder.png'
    },

    item_price(item) {
      return round_up(markup(item.product.price.total_tax_free).live_price)
    },

    occupancy_label(rec) {
      const adults = rec.number_of_adults || 0
      let label = `Soluzione per ${adults} ${adults === 1 ? 'adulto' : 'adulti'}`
      const children = (rec.children && rec.children.length) || 0
      if (children) label += ` e ${children} ${children === 1 ? 'bambino' : 'bambini'}`
      return label
    },

    open_modal_recommended(id) {
      this.$bvModal.show('modal_room_recommended_' + id)
    },

    select_recommended(item) {
      const qty = this.availabilities.recommendation.products
        .filter(rp => rp.id === item.product.id).length

      window.eventBus.$emit('recommended:open_room', item.room.id)
      window.eventBus.$emit('recommended:preselect_product', {
        room_id: item.room.id,
        product_id: item.product.id,
        qty,
      })
    },

  },
}
</script>

<style lang="scss" scoped>

  .recommended {
    @extend %section;
    @extend %section_margin;

    &__element {
      position: relative;

      & + & {
        margin-top: 10px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &__body {
      width: 100%;
    }

    &__title {
      color: $primary;
      font-weight: $vb-font-weight-semibold;
      font-size: $font-size-xl;
      background-color: #f4f8ff;
      border-bottom: 1px solid rgba($primary, .2);
      padding: 12px 20px;
      margin-bottom: 0;
    }

    &__row {
      padding: $grid-gutter-20;
      display: flex;
      flex-direction: column;

      @include media-breakpoint-up(md){
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }

    &__main {
      flex: 1 1 auto;
    }

    &__name {
      font-size: $font-size-lg;
      font-weight: $vb-font-weight-semibold;
      text-decoration: underline;
      margin-bottom: 10px;
    }

    &__details {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px;
      margin-bottom: 10px;
    }

    &__beds {
      display: flex;
      align-items: center;
      font-size: $font-size-md;
      color: $secondary;
      font-weight: $vb-font-weight-semibold;

      &-icon {
        margin-right: 5px;
      }
    }

    &__occupancy {
      display: inline-block;
      background-color: $wild-sand;
      border-radius: $border-radius-lg;
      padding: 3px 10px;
      font-size: $font-size-sm;
    }

    &__cta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      margin-top: 15px;

      @include media-breakpoint-up(md){
        margin-top: 0;
        margin-left: 20px;
        width: 200px;
      }
    }

    &__price {
      display: flex;
      flex-direction: column;
      text-align: right;
      margin-bottom: 10px;

      &-value {
        font-size: $font-size-xl;
        color: $primary;
        font-weight: $vb-font-weight-semibold;
      }

      &-tax {
        font-size: $font-size-sm;
        color: $gray-500;
        line-height: normal;
      }
    }

    ::v-deep .vb-card__wrap {
      padding: 0;
      overflow: hidden;
    }

    ::v-deep .vb-card__element--image {
      @include media-breakpoint-up(xl){
        flex: 0 0 25%;
        max-width: 25%;
      }
    }

    ::v-deep .vb-card__element--wrap {
      @include media-breakpoint-up(xl){
        flex: 0 0 75%;
        max-width: 75%;
      }
    }
  }

</style>
